import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material"
import { FilterList } from "@mui/icons-material"

import { DEFAULT_VEHICLE_FILTERS, hasActiveFilters } from "../../../utils/vehicleFilters"

const selectSx = {
    bgcolor: "#fff",
    borderRadius: 1.5,
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0, 4, 53, 0.12)",
    },
}

const VehicleFilters = ({ filters, options, onChange, resultCount }) => {
    const modelOptions = filters.make
        ? options.modelsByMake[filters.make] || []
        : options.models

    const active = hasActiveFilters(filters)

    const update = (key) => (event) => {
        const value = event.target.value
        const next = { ...filters, [key]: value }

        if (key === "make" && value !== filters.make) {
            next.model = ""
        }

        onChange(next)
    }

    return (
        <Box
            sx={{
                mb: 3,
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                background: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(0, 4, 53, 0.08)",
                boxShadow: "0 8px 24px rgba(0, 4, 53, 0.04)",
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1.5}
                mb={2}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <FilterList sx={{ color: "#00A3FF", fontSize: 22 }} />
                    <Box>
                        <Typography variant="subtitle1" fontWeight={800} color="#000435" lineHeight={1.2}>
                            Filter vehicles
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {resultCount} vehicle{resultCount === 1 ? "" : "s"} match
                            {active ? " your filters" : " in the fleet"}
                        </Typography>
                    </Box>
                </Stack>

                {active && (
                    <Button
                        variant="text"
                        onClick={() => onChange({ ...DEFAULT_VEHICLE_FILTERS })}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            color: "#000435",
                            alignSelf: { xs: "flex-start", sm: "center" },
                        }}
                    >
                        Clear filters
                    </Button>
                )}
            </Stack>

            <Box
                sx={{
                    display: "grid",
                    gap: 1.5,
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                        lg: "repeat(3, 1fr)",
                    },
                }}
            >
                <FormControl size="small" fullWidth sx={selectSx}>
                    <InputLabel id="filter-authority-label">Licensing authority</InputLabel>
                    <Select
                        labelId="filter-authority-label"
                        label="Licensing authority"
                        value={filters.authority}
                        onChange={update("authority")}
                    >
                        <MenuItem value="">All authorities</MenuItem>
                        {options.authorities.map((authority) => (
                            <MenuItem key={authority} value={authority}>
                                {authority}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth sx={selectSx}>
                    <InputLabel id="filter-make-label">Make</InputLabel>
                    <Select
                        labelId="filter-make-label"
                        label="Make"
                        value={filters.make}
                        onChange={update("make")}
                    >
                        <MenuItem value="">All makes</MenuItem>
                        {options.makes.map((make) => (
                            <MenuItem key={make} value={make}>
                                {make}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth sx={selectSx} disabled={modelOptions.length === 0}>
                    <InputLabel id="filter-model-label">Model</InputLabel>
                    <Select
                        labelId="filter-model-label"
                        label="Model"
                        value={filters.model}
                        onChange={update("model")}
                    >
                        <MenuItem value="">All models</MenuItem>
                        {modelOptions.map((model) => (
                            <MenuItem key={model} value={model}>
                                {model}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth sx={selectSx}>
                    <InputLabel id="filter-fuel-label">Fuel type</InputLabel>
                    <Select
                        labelId="filter-fuel-label"
                        label="Fuel type"
                        value={filters.fuel}
                        onChange={update("fuel")}
                    >
                        <MenuItem value="">All fuel types</MenuItem>
                        {options.fuels.map((fuel) => (
                            <MenuItem key={fuel} value={fuel}>
                                {fuel}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth sx={selectSx}>
                    <InputLabel id="filter-tag-label">Tag</InputLabel>
                    <Select
                        labelId="filter-tag-label"
                        label="Tag"
                        value={filters.tag}
                        onChange={update("tag")}
                    >
                        <MenuItem value="">All tags</MenuItem>
                        {options.tags.map((tag) => (
                            <MenuItem key={tag} value={tag}>
                                {tag}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth sx={selectSx}>
                    <InputLabel id="filter-insurance-label">Insurance</InputLabel>
                    <Select
                        labelId="filter-insurance-label"
                        label="Insurance"
                        value={filters.insurance}
                        onChange={update("insurance")}
                    >
                        <MenuItem value="">Any</MenuItem>
                        <MenuItem value="included">Available with insurance</MenuItem>
                        <MenuItem value="not_included">Available without insurance</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        </Box>
    )
}

export default VehicleFilters
